import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { emailRegex, isValidWebsiteURL } from '@percent/utility'
import { TimePickerHelpers } from '@percent/domain/giving'

export const DEFAULT_MAX_STRING_LENGTH = 255
export const DEFAULT_URL_MAX_LENGTH = 2048

export const checkIfDateIsInTheFuture = (date: Date) => dayjs(date).isAfter(dayjs())

export const checkIfEndDateIsAfterStart = (start: Date, end: Date) => dayjs(end).isAfter(dayjs(start))

export const checkTimeFormatValidForFurtherChecks = (time: string) =>
  !!time.match(/^(?:\d?\d):[0-5]\d(?:\s?[apAP][mM])?$/)

export function useSharedValidationRules() {
  const { t } = useTranslation()

  const validateString = ({
    maxLength = DEFAULT_MAX_STRING_LENGTH,
    optional = false
  }: {
    maxLength?: number
    optional?: boolean
  } = {}) =>
    Yup.string()
      .trim()
      .when([], {
        is: () => !!maxLength,
        then: schema =>
          schema.max(
            maxLength || DEFAULT_MAX_STRING_LENGTH,
            t('validation.maxCharacters', {
              max: maxLength || DEFAULT_MAX_STRING_LENGTH
            })
          )
      })
      .when([], {
        is: () => !optional,
        then: Yup.string().trim().required(t('validation.requiredField'))
      })

  const validateDate = ({
    shouldBeInTheFuture = false,
    optional = false
  }: {
    shouldBeInTheFuture?: boolean
    optional?: boolean
  } = {}) =>
    Yup.date()
      .nullable()
      .when([], {
        is: () => shouldBeInTheFuture,
        then: schema =>
          schema.test('startDateInTheFuture', t('validation.dateInTheFuture'), value => {
            if (value) {
              return checkIfDateIsInTheFuture(value)
            }

            return true
          })
      })
      .when([], {
        is: () => !optional,
        then: Yup.date().required(t('validation.requiredField'))
      })

  const validateEmail = (optional = false) =>
    Yup.string()
      .trim()
      .max(255, t('validation.maxCharacters', { max: 255 }))
      .matches(emailRegex, t('validation.invalidEmailAddress'))
      .when([], {
        is: () => !optional,
        then: schema => schema.required(t('validation.requiredField'))
      })

  const validateUrl = (optional = false) =>
    isValidWebsiteURL(t('validation.url'))
      .trim()
      .max(DEFAULT_URL_MAX_LENGTH, t('validation.maxCharacters', { max: DEFAULT_URL_MAX_LENGTH }))
      .when([], {
        is: () => !optional,
        then: schema => schema.required(t('validation.requiredField'))
      })

  const validateNumber = ({
    min,
    max,
    mustBePositive = true,
    optional = false,
    canEqualMax = false
  }: {
    min?: number
    max?: number
    mustBePositive?: boolean
    optional?: boolean
    canEqualMax?: boolean
  } = {}) =>
    Yup.number()
      .nullable()
      .when([], {
        is: () => mustBePositive,
        then: schema => schema.positive(t('validation.positive'))
      })
      .when([], {
        is: () => typeof min === 'number',
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        then: schema => schema.min(min!, t('validation.minAmount', { min }))
      })
      .when([], {
        is: () => typeof max === 'number',
        then: schema =>
          schema.max(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            max!,
            canEqualMax ? t('validation.maxOrEqualAmount', { max }) : t('validation.maxAmount', { max })
          )
      })
      .when([], {
        is: () => !optional,
        then: schema => schema.required(t('validation.requiredField'))
      })

  const validateTimeString = () =>
    Yup.string()
      .trim()
      .test('isValidTime', t('validation.timeFormat'), (value: string | undefined) => {
        if (value) {
          const is12HrsFormat = TimePickerHelpers.is12hourFormat()

          return is12HrsFormat ? TimePickerHelpers.isValid12HourTime(value) : TimePickerHelpers.isValid24HourTime(value)
        }

        return true
      })

  return {
    validateString,
    validateDate,
    validateEmail,
    validateUrl,
    validateNumber,
    validateTimeString
  }
}
